import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, ArrowDown, Phone, Send, CheckCircle2, Loader2, CalendarCheck } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportingSection = () => {
  const { t, lang } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", description: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [meetingData, setMeetingData] = useState({ name: "", email: "", reason: "", preferred_datetime: "" });
  const [meetingSubmitted, setMeetingSubmitted] = useState(false);
  const [meetingSubmitting, setMeetingSubmitting] = useState(false);
  const [meetingError, setMeetingError] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getStepStyle = (type) => {
    switch (type) {
      case "start":
        return "bg-[#1a1a2e] text-white";
      case "action":
        return "bg-white text-[#1a1a2e] border-2 border-[#E8792B]";
      case "decision":
        return "bg-[#E8792B] text-white";
      case "urgent":
        return "bg-red-600 text-white";
      case "end":
        return "bg-[#27AE60] text-white";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/reports`, { ...formData, language: lang });
      setSubmitted(true);
    } catch {
      setSubmitError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    setMeetingError(null);
    setMeetingSubmitting(true);
    try {
      await axios.post(`${API}/meetings`, { ...meetingData, language: lang });
      setMeetingSubmitted(true);
    } catch {
      setMeetingError(t("meeting.formError"));
    } finally {
      setMeetingSubmitting(false);
    }
  };

  const steps = t("reporting.steps") || [];

  return (
    <section id="reporting" ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            {t("reporting.title")}
          </h2>
          <div className="w-20 h-1 bg-[#E8792B] rounded-full mb-6" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              {t("reporting.mainText")}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-red-800 mb-1">{t("reporting.emergencyTitle")}</h4>
                  <p className="text-red-700 text-sm leading-relaxed">
                    {t("reporting.emergencyNote")}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed">
              {t("reporting.investigationNote")}
            </p>

            {/* Mandatory Reporting + Who Reports */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h4 className="font-bold text-amber-900 mb-2 text-sm uppercase tracking-wide">
                  {t("reportingExtra.mandatoryTitle")}
                </h4>
                <p className="text-amber-900/80 text-sm leading-relaxed">
                  {t("reportingExtra.mandatoryText")}
                </p>
              </div>
              <div className="bg-[#1a1a2e] rounded-xl p-5 text-white">
                <h4 className="font-bold text-[#E8792B] mb-3 text-sm uppercase tracking-wide">
                  {t("reportingExtra.whoReportsTitle")}
                </h4>
                <ul className="space-y-2">
                  {(t("reportingExtra.whoReports") || []).map((line, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-white/85 leading-relaxed">
                      <span className="text-[#E8792B] mt-1 flex-shrink-0">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div
              className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              data-testid="report-form-card"
            >
              <h3 className="text-lg font-bold text-[#1a1a2e] mb-4">
                {t("reporting.formTitle")}
              </h3>

              {submitted ? (
                <div
                  className="flex flex-col items-center justify-center py-8 text-center"
                  data-testid="report-success"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-bold text-[#1a1a2e] mb-2">
                    {t("reporting.formSuccess")}
                  </h4>
                  <p className="text-gray-500 text-sm mb-4">{t("reporting.formSuccessMsg")}</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", mobile: "", description: "" });
                    }}
                    data-testid="report-another-button"
                    className="text-[#E8792B] text-sm font-medium hover:underline"
                  >
                    {t("reporting.formAnother")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="report-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("reporting.formName")}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t("reporting.formNamePlaceholder")}
                      data-testid="report-name-input"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("reporting.formEmail")}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t("reporting.formEmailPlaceholder")}
                      data-testid="report-email-input"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("reporting.formMobile")}{" "}
                      <span className="text-gray-400 font-normal">
                        ({t("reporting.formOptional")})
                      </span>
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      placeholder={t("reporting.formMobilePlaceholder")}
                      data-testid="report-mobile-input"
                      dir="ltr"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("reporting.formDescription")}
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder={t("reporting.formDescriptionPlaceholder")}
                      data-testid="report-description-input"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all resize-none"
                    />
                  </div>
                  {submitError && (
                    <p className="text-sm text-red-600" data-testid="report-error">
                      {submitError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="report-submit-button"
                    className="w-full flex items-center justify-center gap-2 bg-[#E8792B] hover:bg-[#d06a20] text-white font-medium py-3 px-6 rounded-lg transition-colors text-sm disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    <span>{t("reporting.formSubmit")}</span>
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Flowchart */}
          <div
            className={`transition-all duration-700 delay-400 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">
                {t("reporting.flowchartTitle")}
              </h3>
              <p className="text-gray-500 text-sm mb-8 whitespace-pre-line">
                {t("reporting.flowchartSubtitle")}
              </p>

              <div className="space-y-3">
                {Array.isArray(steps) &&
                  steps.map((step, index) => (
                    <React.Fragment key={index}>
                      <div
                        className={`rounded-xl px-6 py-4 text-center font-medium text-sm shadow-sm transition-all duration-300 hover:scale-[1.02] ${getStepStyle(step.type)}`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {step.type === "urgent" && <Phone className="w-4 h-4" />}
                          {step.text}
                        </div>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="flex justify-center">
                          <ArrowDown className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
              </div>
            </div>

            {/* Schedule a Meeting card */}
            <div
              className="mt-6 bg-[#1a1a2e] rounded-xl p-6 shadow-sm border border-[#1a1a2e] text-white"
              data-testid="meeting-card"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#E8792B]/20 flex items-center justify-center flex-shrink-0">
                  <CalendarCheck className="w-5 h-5 text-[#E8792B]" />
                </div>
                <div>
                  <p className="text-[#E8792B] text-xs font-semibold uppercase tracking-wider mb-1">
                    {t("meeting.eyebrow")}
                  </p>
                  <h3 className="text-lg font-bold text-white leading-tight">
                    {t("meeting.title")}
                  </h3>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                {t("meeting.description")}
              </p>

              {meetingSubmitted ? (
                <div
                  className="flex flex-col items-center justify-center py-6 text-center"
                  data-testid="meeting-success"
                >
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-7 h-7 text-green-400" />
                  </div>
                  <h4 className="text-base font-bold text-white mb-2">
                    {t("meeting.formSuccess")}
                  </h4>
                  <p className="text-white/70 text-sm mb-3">{t("meeting.formSuccessMsg")}</p>
                  <button
                    onClick={() => {
                      setMeetingSubmitted(false);
                      setMeetingData({ name: "", email: "", reason: "", preferred_datetime: "" });
                    }}
                    data-testid="meeting-another-button"
                    className="text-[#E8792B] text-sm font-medium hover:underline"
                  >
                    {t("meeting.formAnother")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleMeetingSubmit}
                  className="space-y-3"
                  data-testid="meeting-form"
                >
                  <input
                    type="text"
                    required
                    value={meetingData.name}
                    onChange={(e) => setMeetingData({ ...meetingData, name: e.target.value })}
                    placeholder={t("meeting.formNamePlaceholder")}
                    aria-label={t("meeting.formName")}
                    data-testid="meeting-name-input"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all"
                  />
                  <input
                    type="email"
                    required
                    value={meetingData.email}
                    onChange={(e) => setMeetingData({ ...meetingData, email: e.target.value })}
                    placeholder={t("meeting.formEmailPlaceholder")}
                    aria-label={t("meeting.formEmail")}
                    data-testid="meeting-email-input"
                    dir="ltr"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all"
                  />
                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">
                      {t("meeting.formDateTime")}{" "}
                      <span className="text-white/40 font-normal">
                        ({t("meeting.formDateTimeOptional")})
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      value={meetingData.preferred_datetime}
                      onChange={(e) =>
                        setMeetingData({ ...meetingData, preferred_datetime: e.target.value })
                      }
                      aria-label={t("meeting.formDateTime")}
                      data-testid="meeting-datetime-input"
                      dir="ltr"
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all [color-scheme:dark]"
                    />
                  </div>
                  <textarea
                    required
                    rows={3}
                    value={meetingData.reason}
                    onChange={(e) => setMeetingData({ ...meetingData, reason: e.target.value })}
                    placeholder={t("meeting.formReasonPlaceholder")}
                    aria-label={t("meeting.formReason")}
                    data-testid="meeting-reason-input"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8792B]/40 focus:border-[#E8792B] transition-all resize-none"
                  />
                  {meetingError && (
                    <p className="text-sm text-red-300" data-testid="meeting-error">
                      {meetingError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={meetingSubmitting}
                    data-testid="meeting-submit-button"
                    className="w-full flex items-center justify-center gap-2 bg-[#E8792B] hover:bg-[#d06a20] text-white font-medium py-3 px-6 rounded-lg transition-colors text-sm disabled:opacity-60"
                  >
                    {meetingSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CalendarCheck className="w-4 h-4" />
                    )}
                    <span>
                      {meetingSubmitting
                        ? t("meeting.formSubmitting")
                        : t("meeting.formSubmit")}
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReportingSection;
